/** Parts of the application pages that the architect and the officer share. */
import {
  Ban,
  CheckCircle2,
  Clock,
  FileEdit,
  Gavel,
  MessageSquare,
  RotateCcw,
  Send,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  Application,
  ApplicationStatus,
  Comment,
  FormValue,
  HistoryEntry,
  Override,
} from "@/api/applications";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { errorMessage } from "@/i18n";
import { formatDateTime, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import { pillClasses, pillIconClasses } from "@/lib/pill";

const STATUS_STYLE: Record<ApplicationStatus, { icon: typeof Clock; classes: string }> = {
  DRAFT: { icon: FileEdit, classes: "border-border bg-muted" },
  SUBMITTED: { icon: Send, classes: "border-info bg-info-soft text-info" },
  UNDER_REVIEW: { icon: Clock, classes: "border-info bg-info-soft text-info" },
  RETURNED: { icon: RotateCcw, classes: "border-warning bg-warning-soft text-warning" },
  APPROVED: { icon: CheckCircle2, classes: "border-success bg-success-soft text-success" },
  REJECTED: { icon: XCircle, classes: "border-error bg-error-soft text-error" },
  REVOKED: { icon: Ban, classes: "border-error bg-error-soft text-error" },
};

/** The application's status as an icon and words, never colour alone. */
export function StatusBadge({
  status,
  large = false,
}: {
  status: ApplicationStatus;
  large?: boolean;
}) {
  const { t } = useTranslation();
  const { icon: Icon, classes } = STATUS_STYLE[status];
  return (
    <span className={pillClasses(large, classes)}>
      <Icon aria-hidden="true" className={pillIconClasses(large)} />
      <span className="text-foreground">{t(`applicationStatus.${status}`)}</span>
    </span>
  );
}

/** Owner, site, zone and use as a short list. */
export function ApplicationFacts({ application }: { application: Application }) {
  const { t } = useTranslation();
  const rows: [string, string][] = [
    [t("application.ownerName"), application.ownerName],
    [t("application.ownerContact"), application.ownerContact],
    [t("application.siteAddress"), application.siteAddress],
    [
      t("application.zoneAndUse"),
      t("preCheck.zoneUse", { zone: application.zone, use: application.use }),
    ],
    [
      t("application.roadWidth"),
      application.roadWidth != null
        ? `${formatNumber(application.roadWidth, 2)} m`
        : t("application.notGiven"),
    ],
    // M44c: only where the road is to be widened.
    ...(application.byelawInputs?.proposedRoadWidth != null
      ? [
          [
            t("application.proposedRoadWidth"),
            `${formatNumber(application.byelawInputs.proposedRoadWidth, 2)} m`,
          ] as [string, string],
        ]
      : []),
    [t("application.architect"), application.architectName],
  ];
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="font-semibold text-muted-foreground">{label}</dt>
          <dd className="break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatValue(field: string, value: number | null | undefined): string {
  if (value == null) return "—";
  return field.endsWith("BAYS") ? String(Math.round(value)) : formatNumber(value, 2);
}

/**
 * The values the form takes from the drawing. The architect may declare a
 * different value, with a reason; the officer sees both side by side.
 */
export function FormValues({
  values,
  onSave,
  saving,
  error,
}: {
  values: FormValue[];
  /** Given when the architect may still change them. */
  onSave?: (overrides: Override[]) => void;
  saving?: boolean;
  error?: unknown;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ value: "", reason: "" });
  const [problem, setProblem] = useState<string | null>(null);
  const overrides: Override[] = values
    .filter((item) => item.source === "ARCHITECT" && item.value != null)
    .map((item) => ({ field: item.field, value: item.value ?? 0, reason: item.reason ?? "" }));

  const save = (field: FormValue["field"]) => {
    const value = Number(draft.value);
    if (draft.value.trim() === "" || !Number.isFinite(value) || value < 0) {
      setProblem(t("application.values.valueInvalid"));
      return;
    }
    if (!draft.reason.trim()) {
      setProblem(t("application.values.reasonRequired"));
      return;
    }
    setProblem(null);
    onSave?.([...overrides.filter((item) => item.field !== field), { field, value, reason: draft.reason.trim() }]);
    setEditing(null);
  };

  return (
    <div className="flex flex-col gap-3">
      {error ? <Alert tone="error">{errorMessage(error)}</Alert> : null}
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{t("application.values.title")}</caption>
          <thead className="bg-muted">
            <tr>
              <th scope="col" className="p-2">{t("application.values.item")}</th>
              <th scope="col" className="p-2 text-right">{t("application.values.measured")}</th>
              <th scope="col" className="p-2 text-right">{t("application.values.value")}</th>
              <th scope="col" className="p-2">{t("application.values.source")}</th>
              {onSave ? <th scope="col" className="p-2"><span className="sr-only">{t("application.values.actions")}</span></th> : null}
            </tr>
          </thead>
          <tbody>
            {values.map((item) => {
              const label = t(`application.fields.${item.field}`);
              return (
                <tr key={item.field} className={cn("border-t border-border align-top", item.source === "ARCHITECT" && "bg-warning-soft")}>
                  <th scope="row" className="p-2 font-semibold">{label}</th>
                  <td className="p-2 text-right tabular-nums">{formatValue(item.field, item.measured)}</td>
                  <td className="p-2 text-right font-semibold tabular-nums">{formatValue(item.field, item.value)}</td>
                  <td className="p-2">
                    {t(`application.sources.${item.source}`)}
                    {item.reason ? <span className="block text-xs text-muted-foreground">{t("application.values.because", { reason: item.reason })}</span> : null}
                  </td>
                  {onSave ? (
                    <td className="p-2">
                      {editing === item.field ? null : (
                        <div className="flex flex-wrap gap-1">
                          <Button variant="link" className="text-sm" onClick={() => { setEditing(item.field); setDraft({ value: item.value != null ? String(item.value) : "", reason: item.reason ?? "" }); setProblem(null); }}>
                            {t("application.values.declare", { item: label })}
                          </Button>
                          {item.source === "ARCHITECT" ? (
                            <Button variant="link" className="text-sm" disabled={saving} onClick={() => { onSave(overrides.filter((o) => o.field !== item.field)); }}>
                              {t("application.values.useMeasured", { item: label })}
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && onSave ? (
        <form
          className="flex flex-col gap-3 rounded-md border border-primary p-4"
          onSubmit={(event) => {
            event.preventDefault();
            save(editing as FormValue["field"]);
          }}
        >
          <p className="font-semibold">{t("application.values.declaring", { item: t(`application.fields.${editing}`) })}</p>
          <label className="flex flex-col gap-1.5 text-sm font-semibold">
            {t("application.values.newValue")}
            <input inputMode="decimal" value={draft.value} onChange={(event) => { setDraft({ ...draft, value: event.target.value }); }} className="min-h-9 rounded-md border border-input bg-background px-3 text-base font-normal" />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-semibold">
            {t("application.values.reason")}
            <textarea rows={2} value={draft.reason} onChange={(event) => { setDraft({ ...draft, reason: event.target.value }); }} className="rounded-md border border-input bg-background px-3 py-2 text-base font-normal" />
          </label>
          {problem ? <p role="alert" className="text-sm font-semibold text-error">{problem}</p> : null}
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>{t("application.values.save")}</Button>
            <Button variant="secondary" onClick={() => { setEditing(null); }}>{t("application.cancel")}</Button>
          </div>
        </form>
      ) : null}
    </div>
  );
} // prettier-ignore

/** The officers' comments, numbered as their markers on the drawing. */
export function CommentList({ comments, onShow }: { comments: Comment[]; onShow?: (comment: Comment) => void }) {
  const { t } = useTranslation();
  if (comments.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("comments.none")}</p>;
  }
  return (
    <ol className="flex flex-col gap-2">
      {comments.map((comment, index) => (
        <li key={comment.id} id={`comment-${String(index + 1)}`} tabIndex={-1} className="flex flex-col gap-1 rounded-md border border-border bg-card p-3">
          <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 rounded bg-violet-700 px-1.5 py-0.5 font-bold text-white">
              <MessageSquare aria-hidden="true" className="size-3" />
              {index + 1}
            </span>
            {t("comments.meta", { author: comment.authorName, date: formatDateTime(comment.createdAt), revision: comment.revisionNumber })}
          </p>
          <p className="whitespace-pre-line text-sm">{comment.body}</p>
          {comment.location && onShow ? (
            <Button variant="link" className="self-start text-sm" onClick={() => { onShow(comment); }}>
              {t("comments.show", { number: index + 1 })}
            </Button>
          ) : null}
        </li>
      ))}
    </ol>
  );
} // prettier-ignore

/** Every decision, newest first. */
export function DecisionList({ application }: { application: Application }) {
  const { t } = useTranslation();
  if (application.decisions.length === 0) {
    return null;
  }
  return (
    <ul className="flex flex-col gap-2">
      {[...application.decisions].reverse().map((decision) => (
        <li key={`${decision.createdAt}-${decision.action}`} className="flex gap-3 rounded-md border border-border bg-card p-3">
          <Gavel aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="flex flex-col gap-1 text-sm">
            <p className="font-semibold">
              {t(decision.officerName === null ? `decision.done.${decision.action}_AUTOMATIC` : `decision.done.${decision.action}`, { officer: decision.officerName, revision: decision.revisionNumber })}
            </p>
            <p className="text-xs text-muted-foreground">{formatDateTime(decision.createdAt)}</p>
            <p className="whitespace-pre-line">{decision.remark}</p>
          </div>
        </li>
      ))}
    </ul>
  );
} // prettier-ignore

/** What happened, oldest first, in plain words. */
export function HistoryList({ items }: { items: HistoryEntry[] }) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("history.none")}</p>;
  }
  return (
    <ol className="flex flex-col gap-2 border-l-2 border-border pl-4">
      {items.map((item, index) => (
        <li key={`${item.occurredAt}-${String(index)}`} className="text-sm">
          <p className="font-semibold">
            {t(`history.actions.${item.action.replace("application.", "")}`, { defaultValue: item.action })}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDateTime(item.occurredAt)}
            {item.actorName ? ` · ${item.actorName}` : ""}
          </p>
        </li>
      ))}
    </ol>
  );
} // prettier-ignore
